import React, { useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { WalletIcon, LandmarkIcon, CreditCardIcon, ShieldCheckIcon, Loader2Icon, XCircleIcon, ArrowLeftIcon } from 'lucide-react';
import { PageShell } from '../components/layout/PageShell';
import { Button } from '../components/ui/Button';
import { SmartImage } from '../components/common/SmartImage';
import { PaymentCategory } from '../components/order/PaymentCategory';
import { useI18n } from '../lib/i18n';
import { useCart } from '../lib/cart';
import { MACHINE, WALLETS, BANKS, CARDS } from '../lib/data';
import { IMAGES } from '../lib/images';
type PayState = 'idle' | 'processing' | 'failed';
export function Payment() {
  const {
    t
  } = useI18n();
  const navigate = useNavigate();
  const {
    machineId
  } = useParams();
  const {
    total,
    typeCount,
    totalQty,
    lines
  } = useCart();
  const id = machineId ?? MACHINE.id;
  const [openCat, setOpenCat] = useState<string>('wallets');
  const [selected, setSelected] = useState<string | null>(null);
  const [state, setState] = useState<PayState>('idle');
  if (lines.length === 0) {
    return <PageShell data-id="element-355" data-mp="2le0jghi2w7e">
        <section className="mx-auto flex min-h-[60vh] max-w-lg flex-col items-center justify-center px-4 text-center" data-id="element-356" data-mp="3k0cdmw3u1qxz">
          <p className="text-lg text-silver/80" data-id="element-357" data-mp="3oydxvkczvwb1">{t('empty_cart')}</p>
          <Link to={'/select/' + id} className="mt-6" data-id="element-358" data-mp="gjc925953yez">
            <Button data-id="element-359" data-mp="gjvg2qx952e8">{t('back_to_menu')}</Button>
          </Link>
        </section>
      </PageShell>;
  }
  const handlePay = () => {
    if (!selected) return;
    setState('processing');
    setTimeout(() => {
      // Demo flow: always succeed. (Failure path preserved for real integration.)
      navigate('/success/TXN-78478783');
    }, 2200);
  };
  const toggleCat = (cat: string) => setOpenCat(prev => prev === cat ? '' : cat);
  return <PageShell data-id="element-360" data-mp="2wa8bkt6alz93">
      <section className="mx-auto max-w-2xl px-4 py-8 sm:px-6" data-id="element-361" data-mp="2waqeg8ngnbpz">
        <button type="button" onClick={() => navigate(-1)} className="mb-4 inline-flex items-center gap-1.5 text-sm text-silver/70 hover:text-white" data-id="element-362" data-mp="2w85czkr2qxsr">
          <ArrowLeftIcon className="h-4 w-4" data-id="element-363" data-mp="xv0qa2z8b5b7" />
          {t('review_your_order')}
        </button>

        <div className="mb-6 flex items-center justify-between" data-id="element-364" data-mp="szlerhop1lhy">
          <h1 className="text-2xl font-bold text-white" data-id="element-365" data-mp="o23fyk4e4mno">
            {t('secure_payment')}
          </h1>
          <span className="flex items-center gap-2 rounded-full border border-[rgba(217,217,217,0.15)] px-3 py-1.5 text-xs text-silver/80" data-id="element-366" data-mp="k2ul1bxm6iqj">
            <SmartImage src={IMAGES.santimpay} alt="SantimPay" className="h-4 w-4 rounded object-contain" data-id="element-367" data-mp="a33t9i0fuhu5" />
            {t('powered_by_santimpay')}
          </span>
        </div>

        <div className="glass-card p-5" data-id="element-368" data-mp="24hczjf9cdhy1">
          {/* Order summary */}
          <div className="flex items-center justify-between border-b border-[rgba(217,217,217,0.12)] pb-4 text-sm" data-id="element-369" data-mp="1zi9x82kqxptr">
            <div className="text-silver/80" data-id="element-370" data-mp="27zu2so49vrv1">
              <p data-id="element-371" data-mp="27ysgfnht3iy0">
                {t('machine')}: <span className="text-white" data-id="element-372" data-mp="2c0e0bssrcxhp">{id}</span>
              </p>
              <p data-id="element-373" data-mp="2byjemc9nx1ck">
                {t('order')}: {typeCount} {t('drink_types')} · {totalQty}{' '}
                {t('items')}
              </p>
            </div>
            <div className="text-right" data-id="element-374" data-mp="2lp47rk6khdo3">
              <p className="text-xs text-silver/60" data-id="element-375" data-mp="2ls9vow3gm9i8">{t('total')}</p>
              <p className="text-xl font-bold text-neon" data-id="element-376" data-mp="1p1r6do9easn7">{total} ETB</p>
            </div>
          </div>

          {state === 'processing' ? <div className="flex flex-col items-center py-10" data-id="element-377" data-mp="1y0uu55jibw81">
              <Loader2Icon className="h-12 w-12 animate-spin text-neon" data-id="element-378" data-mp="1xzjot1aeyget" />
              <p className="mt-4 text-lg font-medium text-white" data-id="element-379" data-mp="230zbsyw5iwzu">
                {t('processing_payment')}
              </p>
            </div> : state === 'failed' ? <div className="flex flex-col items-center py-10 text-center" data-id="element-380" data-mp="3eebh7e7k7no4">
              <XCircleIcon className="h-12 w-12 text-[#FF6B6B]" data-id="element-381" data-mp="r6nk6e1netyy" />
              <p className="mt-4 text-lg font-semibold text-white" data-id="element-382" data-mp="r6x87w34ur20">
                {t('payment_failed')}
              </p>
              <div className="mt-6 flex gap-3" data-id="element-383" data-mp="m9wzteit9c7q">
                <Button onClick={() => setState('idle')} data-id="element-384" data-mp="108ephhsa0ysa">
                  {t('retry_payment')}
                </Button>
                <a href="https://t.me/Revov_bot" target="_blank" rel="noopener noreferrer" data-id="element-385" data-mp="v6xm9fmbl08j">
                  <Button variant="secondary" data-id="element-386" data-mp="15780n3k3cdpa">{t('telegram_support')}</Button>
                </a>
              </div>
            </div> : <>
              <p className="mb-4 mt-4 text-sm font-medium text-silver/80" data-id="element-387" data-mp="1ew80sqabxf52">
                {t('select_payment_method')}
              </p>
              <div className="space-y-3" data-id="element-388" data-mp="1sx2dvm8bo4a2">
                <PaymentCategory title={t('wallets_title')} Icon={WalletIcon} methods={WALLETS} open={openCat === 'wallets'} onToggle={() => toggleCat('wallets')} selected={selected} onSelect={setSelected} data-id="element-389" data-mp="1stwpk97ip6fe" />
                <PaymentCategory title={t('banks_title')} Icon={LandmarkIcon} methods={BANKS} open={openCat === 'banks'} onToggle={() => toggleCat('banks')} selected={selected} onSelect={setSelected} data-id="element-390" data-mp="1xlblvjzmx66f" />
                <PaymentCategory title={t('cards_title')} description={t('cards_desc')} Icon={CreditCardIcon} methods={CARDS} open={openCat === 'cards'} onToggle={() => toggleCat('cards')} selected={selected} onSelect={setSelected} data-id="element-391" data-mp="1tl1crygjpznc" />
              </div>

              <div className="mt-6" data-id="element-392" data-mp="19wsdl4gl4tec">
                <Button pulse fullWidth disabled={!selected} onClick={handlePay} data-id="element-393" data-mp="3chw8z67fvnur">
                  {t('pay')} {total} ETB
                </Button>
              </div>

              <div className="mt-4 flex items-center justify-center gap-2 text-xs text-silver/60" data-id="element-394" data-mp="32pgrsb3yv3ax">
                <ShieldCheckIcon className="h-4 w-4 text-[#4ADE80]" data-id="element-395" data-mp="2xqvscdwjgnnj" />
                {t('secured_by')} · {t('encrypted_note')}
              </div>
            </>}
        </div>
      </section>
    </PageShell>;
}